import isNil from "lodash/isNil";
import { ProfileItem } from "PFApp/components/profile_item";
import { buildProfilesOptionsFetcher } from "PFApp/helpers/options/profiles_options";
import { Select, SelectProps } from "PFComponents/select";
import { Option } from "PFTypes/options";
import { useCallback, useEffect, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { BookingFormData, BookingFormDataCreateMode, BookingFormMode } from "../booking_form.types";
import { BookingFormBasicProfile, BookingFormValues } from "../booking_form_provider";
import { BulkSelectItem } from "./bulk_select_item";
import css from "./workforce_member.module.scss";
import {
  getGroupBookingId,
  getProfilesLimit,
  GroupBookingOption,
  isGroupOption
} from "./workforce_member_select.utils";

const WM_SELECTOR_MIN_HEIGHT = 74; // the height of the selector when an option is selected (to prevent flickering the layout)

type WorkforceMemberSelectProps = {
  initialData: Partial<BookingFormData>;
  mode: BookingFormMode;
};

export const WorkforceMemberSelectDropdown = ({ initialData, mode }: WorkforceMemberSelectProps) => {
  const { t } = useTranslation("bookingModule");
  const { setValue, control, clearErrors } = useFormContext<BookingFormValues>();
  const [workforceMember, bookingType] = useWatch<BookingFormValues, ["workforceMember", "bookingType"]>({
    name: ["workforceMember", "bookingType"]
  });

  const groupBookingOption = useMemo<GroupBookingOption | null>(() => {
    if (mode !== BookingFormMode.Create) {
      return null;
    }
    const groupData = (initialData as BookingFormDataCreateMode).groupBookingData;
    if (isNil(groupData) || isNil(groupData?.searchId)) {
      return null;
    }

    const limit = getProfilesLimit(bookingType);
    const bulkOptionDisabled = (groupData.total ?? 0) > limit;

    return {
      searchId: groupData.searchId,
      displayElement: <BulkSelectItem count={groupData.total || 0} disabled={bulkOptionDisabled} />,
      disabled: bulkOptionDisabled
    };
  }, [mode, initialData, bookingType]);

  useEffect(() => {
    if (!isGroupOption(workforceMember)) {
      return;
    }
    if (groupBookingOption?.disabled) {
      setValue("workforceMember", undefined, { shouldValidate: true });
    } else {
      setValue("workforceMember", groupBookingOption ?? undefined, { shouldValidate: true });
    }
  }, [groupBookingOption]);

  const fetchOptions = useCallback<
    NonNullable<SelectProps<BookingFormBasicProfile | GroupBookingOption>["fetchOptions"]>
  >(
    async ({ ids, searchTerm }) => {
      const fetchProfileOptions = buildProfilesOptionsFetcher({
        profileVisibility: "active",
        renderDisplayElement: (profile) => (
          <ProfileItem profile={profile} classes={{ content: css.profileItemContent }} />
        )
      });
      const response = await fetchProfileOptions({ ids, searchTerm });
      const sortedEntries = [...response.entries].sort((optionA, optionB) =>
        (optionA.original?.firstName ?? "").localeCompare(optionB.original?.firstName ?? "")
      );

      const groupOptions = groupBookingOption
        ? [
            {
              id: getGroupBookingId(groupBookingOption.searchId),
              value: !groupBookingOption.disabled
                ? t("bookings.create.bulk.title")
                : `${t("bookings.create.bulk.title")} - ${t("bookings.create.bulk.tooManyProfiles", {
                    count: getProfilesLimit(bookingType)
                  })}`,
              displayElement: groupBookingOption.displayElement,
              disabled: groupBookingOption.disabled,
              original: groupBookingOption
            }
          ]
        : [];

      return {
        ...response,
        entries: [...groupOptions, ...sortedEntries]
      };
    },
    [groupBookingOption, t, bookingType]
  );

  return (
    <div style={{ minHeight: workforceMember ? WM_SELECTOR_MIN_HEIGHT : undefined }}>
      <Controller
        control={control}
        name="workforceMember"
        rules={{
          required: t("bookings.create.workforceMemberRequired")
        }}
        render={({ field, fieldState: { error } }) => {
          const value = isGroupOption(field.value)
            ? getGroupBookingId(field.value.searchId)
            : field.value?.id;
          return (
            <Select<BookingFormBasicProfile | GroupBookingOption>
              dropdownId={`booking-form-workforce-member-${groupBookingOption?.searchId}-${bookingType}`} // dropdownId is needed to trigger refetching when searchId/bookingType changes (may have different total/limit)
              value={isGroupOption(field.value) ? getGroupBookingId(field.value.searchId) : field.value?.id}
              onChange={(option: Option<BookingFormBasicProfile | GroupBookingOption>) => {
                field.onChange(option.original);
                clearErrors("workforceMember");
              }}
              fetchOptions={fetchOptions}
              label={t("bookings.create.workforceMember")}
              required
              onClear={value && mode === BookingFormMode.Create ? () => field.onChange(null) : undefined}
              error={error?.message}
            />
          );
        }}
      />
    </div>
  );
};
